import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Typography,
  Box,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import Layout from "../../../components/Layout";
import { useAppContext } from "../../../context/AppContext";
import { ContactInfo } from "../../../types";
import ApiService, { ICountryData } from "../../../services/api";
import { useLogoSrc } from "../../../hooks/useLogoSrc";
import { useFormValidation } from "./hooks/useFormValidation";
import { EmailVerificationModal } from "./components/EmailVerificationModal";
import { NewPasswordModal } from "./components/NewPasswordModal";
import { LoginForm } from "./components/LoginForm";
import { SignupForm } from "./components/SignupForm";
import { ForgotPasswordForm } from "./components/ForgotPasswordForm";
import {
  getCountryNameFromId,
  findCountryByName,
  findDefaultCountry,
} from "./utils/countryUtils";


// Country data will be fetched from API


// Types
interface SignupData {
  first_name: string;
  last_name: string;
  email: string;
  country_id: string;
  phone_number: string;
  password: string;
  confirmPassword: string;
}

interface SignupErrors {
  [key: string]: string;
}


// Using shared common styles from styles.ts

// Components



const ContactPage: React.FC = () => {
  const {
    setCurrentView,
    sessionId,
    setSnackbar,
    currentView,
    contactInfo,
    setToken,
    setUserId,
    setRole,
    redirectAfterAuth,
    setRedirectAfterAuth,
    distributorId,
    role,
    fetchUserProfiles,
  } = useAppContext();
  const { getTranslation, currentLanguage } = useAppContext();
  const logoSrc = useLogoSrc();
  const { 
    validateEmail, 
    validateStrongPassword, 
    validatePasswordConfirmation, 
    validateLoginPassword, 
    validateRequired,
    validateRequiredfirst,
    validateRequiredlast,
    validateRequiredcountry,
    validatePhoneNumber
  } = useFormValidation();

  // Function to call getUserProfiles API using cached function
  const callGetUserProfiles = useCallback(async (token: string) => {
    if (!token) {
      return;
    }

    try {
      const api = new ApiService(null, token);
      await fetchUserProfiles(api, role as 1 | 2);
    } catch (error: any) {
      console.error('Failed to fetch user profiles:', error);
    }
  }, [role, fetchUserProfiles]);

  // State management
  const [formState, setFormState] = useState({
    showPassword: false,
    showConfirmPassword: false,
    signupStep: "email" as "email" | "password",
    showEmailVerificationModal: false,
    showNewPasswordModal: false,
    emailVerificationCode: "",
    codeDigits: ["", "", "", ""],
    resendLoading: false,
    emailVerified: false,
    resendCountdown: 0, // 2 minutes = 120 seconds
  });

  const [formData, setFormData] = useState<ContactInfo>({
    email: contactInfo.email || "",
    password: contactInfo.password || "",
  });

  const [signupData, setSignupData] = useState<SignupData>({
    first_name: "",
    last_name: "",
    email: "",
    country_id: "",
    phone_number: "",
    password: "",
    confirmPassword: "",
  });

  const performSignupRef = useRef<(() => Promise<void>) | null>(null);
  const shouldProceedWithSignupRef = useRef<boolean>(false);
  /** Sync default `country_id` applied immediately; IP refinement only overwrites if still this id. */
  const signupSyncDefaultCountryIdRef = useRef<string | null>(null);

  // State for countries data from API
  const [countries, setCountries] = useState<ICountryData[]>([]);
  const [countriesLoading, setCountriesLoading] = useState<boolean>(true);
  const [flagsLoading, setFlagsLoading] = useState<{ [key: string]: boolean }>({});

  const [signupErrors, setSignupErrors] = useState<SignupErrors>({});
  const [loginErrors, setLoginErrors] = useState<{ [key: string]: string }>({});

  const [newPasswordData, setNewPasswordData] = useState({
    newPassword: "",
    confirmNewPassword: "",
  });

  // Loading states for API calls
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isEmailVerificationLoading, setIsEmailVerificationLoading] = useState(false);
  const [isVerifyEmailLoading, setIsVerifyEmailLoading] = useState(false);
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [isNewPasswordLoading, setIsNewPasswordLoading] = useState(false);
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);
  const [otpRetryCount, setOtpRetryCount] = useState(0);
  const [isRetryingOtp, setIsRetryingOtp] = useState(false);

  // Consent state for privacy policy
  const [privacyConsent, setPrivacyConsent] = useState<boolean>(false);

  // Error data popup state for deactivated user
  const [errorDataPopup, setErrorDataPopup] = useState<{
    open: boolean;
    email?: string;
    phone_number?: string;
  }>({
    open: false,
  });

  // Memoized values
  const isSignupStep = useMemo(() => currentView === "signup", [currentView]);
  const isContactStep = useMemo(() => currentView === "contact", [currentView]);
  const isForgotPasswordStep = useMemo(
    () => currentView === "forgot-password",
    [currentView]
  );

  useEffect(() => {
    // Check if we're currently restoring from privacy policy
    const isRestoringFromPrivacy = sessionStorage.getItem('isRestoringFromPrivacy') === 'true';
    
    // Check if we're returning from privacy policy
    const privacyPolicyPrevView = sessionStorage.getItem('privacyPolicyPrevView');
    const storedFormData = sessionStorage.getItem('signupFormData');
    const isReturningFromPrivacy = privacyPolicyPrevView !== null && 
                                   privacyPolicyPrevView.startsWith('signup-') &&
                                   storedFormData !== null;
    
    if (isReturningFromPrivacy || isRestoringFromPrivacy) {
      // Don't reset signup data - let the privacy policy restoration logic handle it
      return;
    }

    // Reset all form states when currentView changes (normal navigation)
    setFormData({
      email: "",
      password: "",
    });

    // Only clear signup data if we're not returning from privacy policy
    if (!isReturningFromPrivacy) {
      signupSyncDefaultCountryIdRef.current = null;
      setSignupData({
        first_name: "",
        last_name: "",
        email: "",
        country_id: "",
        phone_number: "",
        password: "",
        confirmPassword: "",
      });
    }

    setNewPasswordData({
      newPassword: "",
      confirmNewPassword: "",
    });

    // Only reset form state if we're not returning from privacy policy
    if (!isReturningFromPrivacy) {
      setFormState({
        showPassword: false,
        showConfirmPassword: false,
        signupStep: "email" as "email" | "password",
        showEmailVerificationModal: false,
        showNewPasswordModal: false,
        emailVerificationCode: "",
        codeDigits: ["", "", "", ""],
        resendLoading: false,
        emailVerified: false,
        resendCountdown: 0,
      });
      
      setPrivacyConsent(false);
    }

    setSignupErrors({});
    setLoginErrors({});
  }, [currentView]);

  // Clear OTP fields whenever email verification modal opens
  useEffect(() => {
    if (formState.showEmailVerificationModal) {
      setFormState((prev) => ({
        ...prev,
        codeDigits: ["", "", "", ""],
        emailVerificationCode: "",
      }));
    }
  }, [formState.showEmailVerificationModal]);

  // Countdown effect for resend OTP
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (formState.resendCountdown > 0) {
      timer = setTimeout(() => {
        setFormState((prev) => ({
          ...prev,
          resendCountdown: prev.resendCountdown - 1,
        }));
      }, 1000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [formState.resendCountdown]);

  // Fetch countries data from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setCountriesLoading(true);
        const apiService = new ApiService();
        const data = await apiService.getCountryData();
        setCountries(data.languageData || []);
      } catch (error) {
        console.error("Failed to fetch countries:", error);
        // Fallback to empty array if API fails
        setCountries([]);
      } finally {
        setCountriesLoading(false);
      }
    };

    fetchCountries();
  }, []);

  // Default country: sync fallback first (fast dial code), then refine from IP in background
  useEffect(() => {
    const isRestoringFromPrivacy = sessionStorage.getItem('isRestoringFromPrivacy') === 'true';
    const hasStoredFormData = sessionStorage.getItem('signupFormData') !== null;

    if (countries.length === 0 || isRestoringFromPrivacy || hasStoredFormData) {
      return;
    }

    let cancelled = false;

    setSignupData((prev) => {
      if (prev.country_id) {
        return prev;
      }
      const fallback = findDefaultCountry(countries);
      if (fallback?._id && typeof fallback._id === 'string') {
        signupSyncDefaultCountryIdRef.current = fallback._id;
        return { ...prev, country_id: fallback._id };
      }
      return prev;
    });

    const refineFromIp = async () => {
      try {
        const api = new ApiService();
        const detectedCountryName = await api.getCountryNameFromIp();
        if (cancelled) return;

        const matched = detectedCountryName
          ? findCountryByName(detectedCountryName, countries)
          : undefined;

        if (!matched?._id || typeof matched._id !== 'string') {
          return;
        }

        const matchedId = matched._id;

        setSignupData((prev) => {
          const syncId = signupSyncDefaultCountryIdRef.current;
          if (syncId != null && prev.country_id !== syncId) {
            return prev;
          }
          if (prev.country_id === matchedId) {
            return prev;
          }
          return { ...prev, country_id: matchedId };
        });
      } catch {
        // Keep sync fallback
      }
    };

    void refineFromIp();

    return () => {
      cancelled = true;
    };
  }, [countries]);

  // Check for stored signup step and form data when returning from privacy policy
  useEffect(() => {
    // Remove debugger statement
    if (isSignupStep) {
      const storedStep = sessionStorage.getItem('signupStep');
      const storedFormData = sessionStorage.getItem('signupFormData');
      
      if (storedStep && (storedStep === 'email' || storedStep === 'password')) {
        // Set a flag to prevent form clearing during restoration
        sessionStorage.setItem('isRestoringFromPrivacy', 'true');
        
        // Restore signup step and email verification status
        const storedEmailVerified = sessionStorage.getItem('emailVerified') === 'true';
        setFormState(prev => ({
          ...prev,
          signupStep: storedStep as "email" | "password",
          emailVerified: storedEmailVerified
        }));
        
        // Restore form data if available
        if (storedFormData) {
          try {
            const parsedFormData = JSON.parse(storedFormData);
            setSignupData(prev => ({
              ...prev,
              ...parsedFormData
            }));
          } catch (error) {
            console.error('Error parsing stored form data:', error);
          }
        }

        if (sessionStorage.getItem('signupPrivacyConsent') === 'true') {
          setPrivacyConsent(true);
        }
        
        // Clear the stored data after restoring
        sessionStorage.removeItem('signupStep');
        sessionStorage.removeItem('signupFormData');
        sessionStorage.removeItem('privacyPolicyPrevView');
        sessionStorage.removeItem('emailVerified');
        sessionStorage.removeItem('signupPrivacyConsent');
        
        // Remove the restoration flag after a short delay to allow state updates
        setTimeout(() => {
          sessionStorage.removeItem('isRestoringFromPrivacy');
        }, 100);
      }
    }
  }, [isSignupStep]);

  // Additional effect to restore form data immediately when component mounts
  useEffect(() => {
    // Check if we have stored form data and we're on signup step
    const storedFormData = sessionStorage.getItem('signupFormData');
    const storedStep = sessionStorage.getItem('signupStep');
    
    if (isSignupStep && storedFormData && storedStep) {
      // Set a flag to prevent form clearing during restoration
      sessionStorage.setItem('isRestoringFromPrivacy', 'true');
      
      try {
        const parsedFormData = JSON.parse(storedFormData);
        
        // Restore form data
        setSignupData(prev => ({
          ...prev,
          ...parsedFormData
        }));
        
        // Restore signup step and email verification status
        const storedEmailVerified = sessionStorage.getItem('emailVerified') === 'true';
        setFormState(prev => ({
          ...prev,
          signupStep: storedStep as "email" | "password",
          emailVerified: storedEmailVerified
        }));

        if (sessionStorage.getItem('signupPrivacyConsent') === 'true') {
          setPrivacyConsent(true);
        }
        
        // Clear the stored data after restoring
        sessionStorage.removeItem('signupStep');
        sessionStorage.removeItem('signupFormData');
        sessionStorage.removeItem('privacyPolicyPrevView');
        sessionStorage.removeItem('emailVerified');
        sessionStorage.removeItem('signupPrivacyConsent');
        
        // Remove the restoration flag after a short delay
        setTimeout(() => {
          sessionStorage.removeItem('isRestoringFromPrivacy');
        }, 100);
        
      } catch (error) {
        console.error('Error parsing stored form data on mount:', error);
        sessionStorage.removeItem('isRestoringFromPrivacy');
      }
    }
  }, []); // Empty dependency array - runs only once on mount

  // Event handlers
  const handleTogglePassword = useCallback(() => {
    setFormState((prev) => ({ ...prev, showPassword: !prev.showPassword }));
  }, []);

  const handleToggleConfirmPassword = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      showConfirmPassword: !prev.showConfirmPassword,
    }));
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Clear errors when user starts typing
      if (signupErrors[name]) {
        setSignupErrors((prev) => ({ ...prev, [name]: "" }));
      }
      if (loginErrors[name]) {
        setLoginErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    [signupErrors, loginErrors]
  );

  const applySignupEmailSuggestion = useCallback((email: string) => {
    setSignupData((prev) => ({ ...prev, email }));
    setSignupErrors((prev) => ({ ...prev, email: "" }));
  }, []);

  const handleSignupChange = useCallback(
    (e: ChangeEvent<HTMLInputElement> | any): void => {
      const { name, value } = e.target;
      const sanitizedValue =
        name === "phone_number" ? value.replace(/\D/g, "") : value;
      
      setSignupData((prev) => {
        const updated = { ...prev, [name]: sanitizedValue };
        
        // If country_id changed, re-validate phone_number
        if (name === "country_id" && prev.phone_number) {
          setTimeout(() => {
            const countryName = getCountryNameFromId(sanitizedValue, countries);
            const phoneError = validatePhoneNumber(prev.phone_number, countryName);
            setSignupErrors((prevErrors) => {
              if (phoneError) {
                return { ...prevErrors, phone_number: phoneError };
              } else if (prevErrors.phone_number) {
                const { phone_number, ...rest } = prevErrors;
                return rest;
              }
              return prevErrors;
            });
          }, 0);
        }
        
        // If phone_number changed, validate it with current country
        if (name === "phone_number" && prev.country_id) {
          setTimeout(() => {
            const countryName = getCountryNameFromId(prev.country_id, countries);
            const phoneError = validatePhoneNumber(sanitizedValue, countryName);
            setSignupErrors((prevErrors) => {
              if (phoneError) {
                return { ...prevErrors, phone_number: phoneError };
              } else if (prevErrors.phone_number) {
                const { phone_number, ...rest } = prevErrors;
                return rest;
              }
              return prevErrors;
            });
          }, 0);
        }
        
        return updated;
      });

      // Clear error when user starts typing
      if (signupErrors[name] && name !== "country_id") {
        setSignupErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    [signupErrors, validatePhoneNumber, countries]
  );

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      // Prevent numeric keys (0-9) and numpad keys
      if (/\d/.test(e.key)) {
        e.preventDefault();
      }
    },
    []
  );

  const handleCodeInput = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, idx: number) => {
      const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 1);
      const newDigits = [...formState.codeDigits];
      newDigits[idx] = value;

      setFormState((prev) => ({
        ...prev,
        codeDigits: newDigits,
        emailVerificationCode: newDigits.join(""),
      }));

      if (value && idx < 3) {
        document.getElementById(`code-input-${idx + 1}`)?.focus();
      } else if (value && idx === 3) {
        // Remove focus from the last input field when code is complete
        e.target.blur();
      }
    },
    [formState.codeDigits]
  );

  const handleCodeKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
      if (e.key === "Backspace") {
        if (formState.codeDigits[idx]) {
          const newDigits = [...formState.codeDigits];
          newDigits[idx] = "";
          setFormState((prev) => ({
            ...prev,
            codeDigits: newDigits,
            emailVerificationCode: newDigits.join(""),
          }));
        } else if (idx > 0) {
          document.getElementById(`code-input-${idx - 1}`)?.focus();
          const newDigits = [...formState.codeDigits];
          newDigits[idx - 1] = "";
          setFormState((prev) => ({
            ...prev,
            codeDigits: newDigits,
            emailVerificationCode: newDigits.join(""),
          }));
        }
        e.preventDefault();
      }
    },
    [formState.codeDigits]
  );

  const handleCodePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData("text");
      const pastedDigits = pastedData.replace(/[^0-9]/g, "").slice(0, 4);
      
      if (pastedDigits.length > 0) {
        const newDigits = ["", "", "", ""];
        for (let i = 0; i < pastedDigits.length && i < 4; i++) {
          newDigits[i] = pastedDigits[i];
        }
        
        setFormState((prev) => ({
          ...prev,
          codeDigits: newDigits,
          emailVerificationCode: newDigits.join(""),
        }));
        
        // Focus the next empty input or the last input if all are filled
        const nextEmptyIndex = newDigits.findIndex((digit) => !digit);
        const focusIndex = nextEmptyIndex === -1 ? 3 : nextEmptyIndex;
        setTimeout(() => {
          document.getElementById(`code-input-${focusIndex}`)?.focus();
        }, 0);
      }
    },
    []
  );

  // const handleGuestChange = useCallback(() => {
  //   setCurrentView("user-info");
  // }, [setCurrentView]);

  // Validation functions
  const validateLoginForm = useCallback((): boolean => {
    const newErrors: { [key: string]: string } = {};

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const passwordError = validateLoginPassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    setLoginErrors(newErrors);

    // Don't show snackbar - let field-level errors display
    return Object.keys(newErrors).length === 0;
  }, [formData.email, formData.password, validateEmail, validateLoginPassword, currentLanguage]);


  const validateSignupAll = useCallback((): boolean => {
    const combinedErrors: SignupErrors = {};

    // Info validations
    const firstNameError = validateRequiredfirst(signupData.first_name);
    if (firstNameError) combinedErrors.first_name = firstNameError;

    const lastNameError = validateRequiredlast(signupData.last_name);
    if (lastNameError) combinedErrors.last_name = lastNameError;

    const emailError = validateEmail(signupData.email);
    if (emailError) combinedErrors.email = emailError;

    const countryCodeError = validateRequiredcountry(signupData.country_id);
    if (countryCodeError) combinedErrors.country_id = countryCodeError;

    // Only validate phone if countries are loaded and country is found
    let phoneError: string | null = null;
    if (countries.length > 0 && signupData.country_id) {
    const countryName = getCountryNameFromId(signupData.country_id, countries);
      if (countryName) {
        phoneError = validatePhoneNumber(signupData.phone_number, countryName);
      }
      // If country not found, skip validation (will validate once countries load)
    } else if (signupData.phone_number && signupData.country_id) {
      // Phone and country are set but countries not loaded yet
      // Don't validate yet - will validate when countries load
    }
    if (phoneError) combinedErrors.phone_number = phoneError;

    // Password validations - use strong password validation for signup
    const passwordError = validateStrongPassword(signupData.password);
    if (passwordError) combinedErrors.password = passwordError;

    // Password confirmation validation
    const confirmPasswordError = validatePasswordConfirmation(signupData.password, signupData.confirmPassword);
    if (confirmPasswordError) combinedErrors.confirmPassword = confirmPasswordError;

    setSignupErrors(combinedErrors);
    return Object.keys(combinedErrors).length === 0;
  }, [signupData, validateRequiredfirst, validateRequiredlast, validateRequiredcountry, validateEmail, validatePhoneNumber, validateStrongPassword, validatePasswordConfirmation, getCountryNameFromId, currentLanguage, countries]);

  // Re-validate form errors when language changes to update error messages
  useEffect(() => {
    // Only re-validate if there are existing errors
    if (Object.keys(signupErrors).length > 0) {
      validateSignupAll();
    }
    if (Object.keys(loginErrors).length > 0) {
      validateLoginForm();
    }
  }, [currentLanguage, validateSignupAll, validateLoginForm, signupErrors, loginErrors]);

  // Re-validate phone number when countries load (for privacy policy restoration)
  useEffect(() => {
    if (countries.length > 0 && signupData.phone_number && signupData.country_id) {
      // Small delay to ensure state is settled
      const timeoutId = setTimeout(() => {
        const countryName = getCountryNameFromId(signupData.country_id, countries);
        if (countryName) {
          const phoneError = validatePhoneNumber(signupData.phone_number, countryName);
          setSignupErrors(prev => {
            if (phoneError) {
              return { ...prev, phone_number: phoneError };
            } else {
              // Clear error if validation passes
              const { phone_number, ...rest } = prev;
              return rest;
            }
          });
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [countries, signupData.phone_number, signupData.country_id, validatePhoneNumber, getCountryNameFromId]);

  // Form submission handlers
  const handleLoginSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();

      if (!validateLoginForm()) {
        return;
      }

      setIsLoginLoading(true);
      try {
        const api = new ApiService(sessionId);
        const signinResponse = await api.signIn(
          formData.email.toLowerCase(),
          formData.password
        );
        const token = (signinResponse as any).data.user.accessToken;
        const userId = (signinResponse as any).data.user.userId;

        // Set token, userId and role in context (this also stores in cookies)
        setToken(token);
        setUserId(userId);
        setRole(2);

        // Call getUserProfiles API to fetch and store user profile data for customer flow
        await callGetUserProfiles(token);
        if (redirectAfterAuth) {
          setCurrentView(redirectAfterAuth);
          setRedirectAfterAuth && setRedirectAfterAuth(null);
        } else {
          setCurrentView("Welcome");
        }
      } catch (error: any) {
        // Extract only the data object (email and phone_number) from error response
        // Try multiple possible error structures
        let errorData = null;
        
        // Try different paths for error data
        // First check the preserved error structure from API service
        if (error?.data) {
          errorData = error.data;
        } else if (error?.response?.data?.error?.data) {
          errorData = error.response.data.error.data;
        } else if (error?.error?.data) {
          errorData = error.error.data;
        } else if (error?.response?.data?.data) {
          errorData = error.response.data.data;
        } else if (error?.response?.data) {
          // If the entire response.data is the error object, check if it has email/phone
          if (error.response.data.email || error.response.data.phone_number) {
            errorData = error.response.data;
          } else if (error.response.data.error) {
            errorData = error.response.data.error;
          }
        } else if (error?.error) {
          // Check if error.error itself has the data
          if (error.error.email || error.error.phone_number || error.error.data) {
            errorData = error.error.data || error.error;
          }
        }
        
        // Extract error code from error.message field (e.g., "Err94")
        let errorCode = null;
        // Priority: error.response.data.error.message > error.error.message > error.response.data.message > error.message
        if (error?.response?.data?.error?.message) {
          errorCode = error.response.data.error.message; // "Err94"
        } else if (error?.error?.message) {
          errorCode = error.error.message; // "Err94"
        } else if (error?.response?.data?.message) {
          errorCode = error.response.data.message;
        } else if (error?.message) {
          errorCode = error.message;
        }
        
        // Show popup with error data (don't update form fields with incorrect data)
        if (errorData && (errorData.email || errorData.phone_number)) {
          setErrorDataPopup({
            open: true,
            email: errorData.email,
            phone_number: errorData.phone_number,
          });
        }

        // Show snackbar with error code if available
        // Translate the error code (e.g., "Err94" -> getTranslation("Err94"))
        const snackbarMessage = errorCode 
          ? getTranslation(errorCode)
          : getTranslation("Txt647");
        
        setSnackbar({
          message: snackbarMessage,
          open: true,
          severity: "error",
        });
      } finally {
        setIsLoginLoading(false);
      }
    },
    [
      validateLoginForm,
      formData.email,
      sessionId,
      setCurrentView,
      setSnackbar,
      redirectAfterAuth,
      setRedirectAfterAuth,
      callGetUserProfiles,
    ]
  );

  const handleEmailVerificationSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      
      // Validate all required fields for email verification
      const newErrors: SignupErrors = {};
      let hasErrors = false;

      const firstNameError = validateRequired(signupData.first_name);
      if (firstNameError) {
        newErrors.first_name = firstNameError;
        hasErrors = true;
      }

      const lastNameError = validateRequired(signupData.last_name);
      if (lastNameError) {
        newErrors.last_name = lastNameError;
        hasErrors = true;
      }

      const emailError = validateEmail(signupData.email);
      if (emailError) {
        newErrors.email = emailError;
        hasErrors = true;
      }

      const countryCodeError = validateRequired(signupData.country_id);
      if (countryCodeError) {
        newErrors.country_id = countryCodeError;
        hasErrors = true;
      }

    const countryName = getCountryNameFromId(signupData.country_id, countries);
    const phoneError = validatePhoneNumber(signupData.phone_number, countryName);
    if (phoneError) {
      newErrors.phone_number = phoneError;
      hasErrors = true;
    }

      // Update errors - merge with existing errors to preserve other field errors
      setSignupErrors(prev => ({ ...prev, ...newErrors }));

      if (hasErrors) {
        return;
      }
      setIsEmailVerificationLoading(true);
      try {
        const api = new ApiService(sessionId);
        await api.sendOtp(
          signupData.email.trim().toLowerCase(),
          signupData.first_name.trim(),
          signupData.last_name.trim(),
          // signupData.country_id.trim()
        );
        setFormState((prev) => ({
          ...prev,
          showEmailVerificationModal: true,
          resendCountdown: 120, // Start 2-minute countdown
          codeDigits: ["", "", "", ""], // Clear OTP fields when opening modal
          emailVerificationCode: "", // Clear OTP code when opening modal
        }));
        setSnackbar({
          message: getTranslation("Txt910"),
          open: true,
          severity: "success",
        });
      } catch (error: any) {
        // Check if this is a template-related error that we can retry
        const isTemplateError = error?.message?.includes("Email template") || 
                               error?.message?.includes("otp_verification") ||
                               error?.message?.includes("Failed to load");
        
        // Retry logic for template errors (max 2 retries)
        if (isTemplateError && otpRetryCount < 2 && !isRetryingOtp) {
          setOtpRetryCount(prev => prev + 1);
          setIsRetryingOtp(true);
          
          // Wait 2 seconds before retrying
          setTimeout(async () => {
            try {
              const api = new ApiService(sessionId);
              await api.sendOtp(
                signupData.email.trim().toLowerCase(),
                signupData.first_name.trim(),
                signupData.last_name.trim(),
                // signupData.country_id.trim()
              );
              
              // Success on retry
              setFormState((prev) => ({
                ...prev,
                showEmailVerificationModal: true,
                resendCountdown: 120,
              }));
              setSnackbar({
                message: getTranslation("Txt910"),
                open: true,
                severity: "success",
              });
              setOtpRetryCount(0); // Reset retry count
            } catch (retryError: any) {
              setSnackbar({
                message: "System temporarily unavailable. Please try again later.",
                open: true,
                severity: "error",
              });
            } finally {
              setIsRetryingOtp(false);
            }
          }, 2000);
          
          return; // Exit early, retry is in progress
        }
        
        setSnackbar({
          message: getTranslation("Err12"),
          open: true,
          severity: "error",
        });
      } finally {
        setIsEmailVerificationLoading(false);
      }
    },
    [
      validateEmail,
      validateRequired,
      validatePhoneNumber,
      signupData.email,
      signupData.first_name,
      signupData.last_name,
      signupData.phone_number,
      signupData.country_id,
      sessionId,
      setSnackbar,
      setSignupErrors,
      getCountryNameFromId,
    ]
  );

  const handleVerifyEmail = useCallback(async () => {
    if (formState.emailVerificationCode.trim()) {
      setIsVerifyEmailLoading(true);
      try {
        const api = new ApiService(sessionId);
        // Use the appropriate email based on current view
        const emailToVerify = isForgotPasswordStep
          ? formData.email.trim().toLowerCase()
          : signupData.email.trim().toLowerCase();
        
        const authData = await api.verifyOtp(
          formState.emailVerificationCode.trim(),
          emailToVerify
        );

        if (authData.otpResponse.is_email_verified) {
          if (isForgotPasswordStep) {
            // For forgot password, close email verification modal and show new password modal
            // Clear password fields when opening the modal
            setNewPasswordData({
              newPassword: "",
              confirmNewPassword: "",
            });
            setFormState((prev) => ({
              ...prev,
              showEmailVerificationModal: false,
              showNewPasswordModal: true,
            }));
            setSnackbar({
              message:
                getTranslation("Txt839"),
              open: true,
              severity: "success",
            });
          } else {
            // Email verified successfully - proceed with signup if password exists
            setFormState((prev) => ({
              ...prev,
              showEmailVerificationModal: false,
              emailVerified: true,
            }));
            setSnackbar({
              message: getTranslation("Txt839"),
              open: true,
              severity: "success",
            });
            
            // If signup was initiated and password exists, proceed with signup
            if (shouldProceedWithSignupRef.current) {
              shouldProceedWithSignupRef.current = false; // Reset flag
              // Small delay to let the success message show
              setTimeout(() => {
                performSignupRef.current?.();
              }, 1000);
            }
          }
        } else {
          setSnackbar({
            message: getTranslation("Err90"),
            open: true,
            severity: "error",
          });
        }
      } catch (error: any) {
        setSnackbar({
          message: getTranslation("Err4"),
          open: true,
          severity: "error",
        });
      } finally {
        setIsVerifyEmailLoading(false);
      }
    } else {
      setSnackbar({
        message: getTranslation("Err2"),
        open: true,
        severity: "error",
      });
    }
  }, [
    formState.emailVerificationCode,
    signupData.email,
    formData.email,
    isForgotPasswordStep,
    sessionId,
    setSnackbar,
    getTranslation,
    setCurrentView,
    formState.signupStep,
  ]);

  // Simple function to perform actual signup
  const performSignup = useCallback(async () => {
    
    // Validate password is not empty before proceeding
    if (!signupData.password || !signupData.password.trim()) {
      setSnackbar({
        message: getTranslation("Txt895") || getTranslation("Err31") || "Password is required",
        open: true,
        severity: "error",
      });
      return;
    }

    setIsSignupLoading(true);
    try {
      const api = new ApiService(sessionId);
      const signupResponse = await api.signUp(
        signupData.email.trim().toLowerCase(),
        signupData.first_name.trim(),
        signupData.last_name.trim(),
        signupData.password.trim(),
        signupData.phone_number.trim(),
        signupData.country_id.trim(),
        distributorId || undefined
      );

      // Set token, userId and role in context (this also stores in cookies)
      setToken(signupResponse.token);
      setUserId(signupResponse.userId);
      setRole(2);

      // Call getUserProfiles API to fetch and store user profile data for customer flow
      await callGetUserProfiles(signupResponse.token);

      if (redirectAfterAuth) {
        setCurrentView(redirectAfterAuth);
        setRedirectAfterAuth && setRedirectAfterAuth(null);
      } else {
        setCurrentView("user-info");
      }
    } catch (error) {
      setSnackbar({
        message: getTranslation("Err81"),
        open: true,
        severity: "error",
      });
    } finally {
      setIsSignupLoading(false);
    }
  }, [signupData, sessionId, setToken, setUserId, setRole, callGetUserProfiles, setSnackbar, setCurrentView, redirectAfterAuth, setRedirectAfterAuth, distributorId, getTranslation]);

  // Store the signup function in ref for use in email verification
  performSignupRef.current = performSignup;

  const handleSignupSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();


      if (!validateSignupAll()) {
        return;
      }

      // If email is not verified, send OTP and show verification modal
      if (!formState.emailVerified) {
        shouldProceedWithSignupRef.current = true; // Set flag to proceed with signup after verification
        await handleEmailVerificationSubmit(e);
        return;
      }

      // If email is verified, proceed with signup
      await performSignup();
    },
    [validateSignupAll, formState.emailVerified, handleEmailVerificationSubmit, performSignup]
  );


  const handleResendCode = useCallback(async () => {
    setFormState((prev) => ({
      ...prev,
      resendLoading: true,
      codeDigits: ["", "", "", ""],
      emailVerificationCode: "",
    }));

    try {
      const api = new ApiService(sessionId);
      if (isForgotPasswordStep) {
        // For forgot password, use the forgot password OTP endpoint
        await api.sendOtpForgotPassword(formData.email.trim().toLowerCase());
      } else {
        // For signup, use the regular OTP endpoint
        await api.sendOtp(
          signupData.email.trim().toLowerCase(),
          signupData.first_name.trim(),
          signupData.last_name.trim(),
          // signupData.country_id.trim()
        );
      }
      // Start 2-minute countdown (120 seconds)
      setFormState((prev) => ({
        ...prev,
        resendLoading: false,
        resendCountdown: 120,
      }));
    } catch (error: any) {
      setFormState((prev) => ({ ...prev, resendLoading: false }));

      setSnackbar({
        message: getTranslation("Err82"),
        open: true,
        severity: "error",
      });
    }
  }, [
    isForgotPasswordStep,
    formData.email,
    signupData.email,
    signupData.first_name,
    signupData.last_name,
    signupData.country_id,
    sessionId,
    setSnackbar,
  ]);

  // New password handlers for forgot password flow
  const handleNewPasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = e.target;
      setNewPasswordData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const validateNewPassword = useCallback((): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Use strong password validation for new password
    const passwordError = validateStrongPassword(newPasswordData.newPassword);
    if (passwordError) newErrors.newPassword = passwordError;

    // Use password confirmation validation
    const confirmPasswordError = validatePasswordConfirmation(newPasswordData.newPassword, newPasswordData.confirmNewPassword);
    if (confirmPasswordError) newErrors.confirmNewPassword = confirmPasswordError;

    const firstError = Object.values(newErrors)[0];
    if (firstError) {
      setSnackbar({ message: firstError, open: true, severity: "error" });
    }
    return Object.keys(newErrors).length === 0;
  }, [newPasswordData, validateStrongPassword, validatePasswordConfirmation, setSnackbar]);

  const handleNewPasswordSubmit = useCallback(async (): Promise<void> => {
    if (!validateNewPassword()) {
      return;
    }

    setIsNewPasswordLoading(true);
    try {
      const api = new ApiService(sessionId);
              await api.resetPassword(
          formData.email.trim().toLowerCase(),
          newPasswordData.newPassword.trim()
        );

      setSnackbar({
        message:
         getTranslation("Txt911"),
        open: true,
        severity: "success",
        },
      );

      // Reset form and go back to contact page
      setNewPasswordData({ newPassword: "", confirmNewPassword: "" });
      setFormState((prev) => ({ ...prev, showNewPasswordModal: false }));
      setCurrentView("contact");
    } catch (error) {
      setSnackbar({
        message: getTranslation("Err83"),
        open: true,
        severity: "error",
      });
    } finally {
      setIsNewPasswordLoading(false);
    }
  }, [
    validateNewPassword,
    formData.email,
    newPasswordData.newPassword,
    sessionId,
    setSnackbar,
    setCurrentView,
  ]);

  // Render functions



  return (
    <Layout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-evenly",
          alignItems: "center",
          height: "100%",
          width: "100%",
          filter:
            formState.showEmailVerificationModal ||
            formState.showNewPasswordModal
              ? "blur(4px)"
              : "none",
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt="Company Logo"
          sx={{
            height: { xs: "3.5rem", sm: "3.5rem", md: "4.5rem" },
            width: "100%",
            objectFit: "contain",
          }}
        />

        {/* {!isForgotPasswordStep && !isSignupStep && (

      )} */}

      {isContactStep && (
        <LoginForm
          email={formData.email}
          password={formData.password || ""}
          showPassword={formState.showPassword}
          loginErrors={loginErrors}
          isLoading={isLoginLoading}
          onEmailChange={handleChange}
          onPasswordChange={handleChange}
          onTogglePassword={handleTogglePassword}
          onSubmit={handleLoginSubmit}
          onForgotPasswordClick={() => setCurrentView("forgot-password")}
        />
      )}
        {isForgotPasswordStep && (
          <ForgotPasswordForm
            email={formData.email}
            loginErrors={loginErrors}
            isLoading={isForgotPasswordLoading}
            sessionId={sessionId}
            onEmailChange={handleChange}
            onValidateEmail={validateEmail}
            onSetLoginErrors={setLoginErrors}
            onSetIsLoading={setIsForgotPasswordLoading}
            onSetSnackbar={setSnackbar}
            onSetFormState={setFormState}
            onSetCurrentView={setCurrentView}
          />
        )}
        {isSignupStep && (
          <SignupForm
            signupData={signupData}
            signupErrors={signupErrors}
            formState={{
              signupStep: formState.signupStep,
              emailVerified: formState.emailVerified,
              showPassword: formState.showPassword,
              showConfirmPassword: formState.showConfirmPassword,
            }}
            countries={countries}
            countriesLoading={countriesLoading}
            flagsLoading={flagsLoading}
            privacyConsent={privacyConsent}
            isEmailVerificationLoading={isEmailVerificationLoading}
            isRetryingOtp={isRetryingOtp}
            isSignupLoading={isSignupLoading}
            onSignupChange={handleSignupChange}
            onNameKeyDown={handleNameKeyDown}
            onSignupSubmit={handleSignupSubmit}
            onEmailVerificationSubmit={handleEmailVerificationSubmit}
            onTogglePassword={handleTogglePassword}
            onToggleConfirmPassword={handleToggleConfirmPassword}
            onPrivacyConsentChange={setPrivacyConsent}
            onSetCurrentView={setCurrentView}
            onSetFlagsLoading={setFlagsLoading}
            onApplySignupEmailSuggestion={applySignupEmailSuggestion}
          />
        )}



        {/* {isContactStep && distributorId && (
          <>
          <Typography
            variant="body2"
            sx={{
              color: "var(--color-text-primary)",
              mt: 1,
            }}
          >
            {getTranslation("Txt12")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "var(--color-text-primary)",
              textDecoration: "underline",
              mt: 1,
              cursor: "pointer",
              fontWeight: 700,
              "&:hover": {
                textDecoration: "underline",
                color: "var(--color-text-primary)",
              },
            }}
            onClick={handleGuestChange}
          >
            {getTranslation("Txt13")}
          </Typography>
          </>
        )} */}
      </Box>

      <EmailVerificationModal
        open={formState.showEmailVerificationModal}
        onClose={() =>
          setFormState((prev) => ({
            ...prev,
            showEmailVerificationModal: false,
          }))
        }
        onVerify={handleVerifyEmail}
        onResend={handleResendCode}
        codeDigits={formState.codeDigits}
        onCodeInput={handleCodeInput}
        onCodeKeyDown={handleCodeKeyDown}
        onCodePaste={handleCodePaste}
        resendLoading={formState.resendLoading}
        resendCountdown={formState.resendCountdown}
        isVerifyLoading={isVerifyEmailLoading}
      />

      <NewPasswordModal
        open={formState.showNewPasswordModal}
        onClose={() => {
          setFormState((prev) => ({ ...prev, showNewPasswordModal: false }));
          // Clear password fields when closing the modal
          setNewPasswordData({
            newPassword: "",
            confirmNewPassword: "",
          });
        }}
        onSubmit={handleNewPasswordSubmit}
        newPassword={newPasswordData.newPassword}
        confirmNewPassword={newPasswordData.confirmNewPassword}
        onPasswordChange={handleNewPasswordChange}
        showNewPassword={formState.showPassword}
        showConfirmNewPassword={formState.showConfirmPassword}
        onToggleNewPassword={handleTogglePassword}
        onToggleConfirmNewPassword={handleToggleConfirmPassword}
        isLoading={isNewPasswordLoading}
      />

      {/* Account Deactivated Popup - Improved UI */}
      <Dialog
        open={errorDataPopup.open}
        onClose={() => setErrorDataPopup({ open: false })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: { xs: '24px', md: '32px' },
            maxWidth: '500px',
          }
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={() => setErrorDataPopup({ open: false })}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'var(--color-text-secondary)',
            '&:hover': {
              backgroundColor: 'var(--color-surface-hover)',
            },
          }}
        >
          <Close />
        </IconButton>

        
        <DialogContent sx={{ padding: '20px 0', minHeight: '100px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
            {/* Main Message */}
            <Typography
              sx={{
                fontSize: { xs: '18px', md: '22px' },
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                fontFamily: 'HerbalifeFont',
                textAlign: 'center',
                lineHeight: 1.6,
              }}
            >
              {getTranslation("Txt987", "Contact your distributor to activate your account.")}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '15px', md: '16px' },
                fontWeight: 400,
                color: 'var(--color-text-secondary)',
                fontFamily: 'HerbalifeFont',
                textAlign: 'center',
                lineHeight: 1.6,
              }}
            >
              {getTranslation("Txt986")}
            </Typography>
            
            {/* Distributor Contact Information */}
            {(errorDataPopup.email || errorDataPopup.phone_number) && (
              <Box sx={{ width: '100%' }}>
                {/* Contact Info Card */}
                <Box
                  sx={{
                    backgroundColor: 'var(--color-background-muted)',
                    borderRadius: '12px',
                    padding: { xs: '20px', md: '24px' },
                    border: '1px solid var(--color-border)',
                  }}
                >
                  {errorDataPopup.email && (
                    <Box sx={{ mb: errorDataPopup.phone_number ? 2 : 0 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 500,
                          color: 'var(--color-text-primary)',
                          fontFamily: 'HerbalifeFont',
                          wordBreak: 'break-word',
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 600, color: 'var(--color-text-secondary)', fontSize: { xs: '13px', md: '18px' } }}>
                          {getTranslation("Txt825", "Email")} :
                        </Box>{' '}
                        {errorDataPopup.email}
                      </Typography>
                    </Box>
                  )}
                  
                  {errorDataPopup.phone_number && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 500,
                          color: 'var(--color-text-primary)',
                          fontFamily: 'HerbalifeFont',
                          wordBreak: 'break-word',
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 600, color: 'var(--color-text-secondary)', fontSize: { xs: '13px', md: '18px' } }}>
                          {getTranslation("Txt77", "Phone Number").split(':')[0]} :
                        </Box>{' '}
                        {errorDataPopup.phone_number}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export { ContactPage };
